import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from './hooks/useToast';
import { useWallet } from './WalletContext';
import './GameEntry.css';

function HalfSangamGame({ gameName, onBack }) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { walletBalance, updateWalletBalance } = useWallet();
  const [status, setStatus] = useState('Open');
  const [availableDigits, setAvailableDigits] = useState([]);
  
  // Open Digit inputs
  const [openDigitInput, setOpenDigitInput] = useState('');
  const [selectedOpenDigit, setSelectedOpenDigit] = useState('');
  const [filteredOpenDigits, setFilteredOpenDigits] = useState([]);
  const [showOpenDigitDropdown, setShowOpenDigitDropdown] = useState(false);
  const openDigitRef = useRef(null);
  
  // Close Digit inputs
  const [closeDigitInput, setCloseDigitInput] = useState('');
  const [selectedCloseDigit, setSelectedCloseDigit] = useState('');
  const [filteredCloseDigits, setFilteredCloseDigits] = useState([]);
  const [showCloseDigitDropdown, setShowCloseDigitDropdown] = useState(false);
  const closeDigitRef = useRef(null);
  
  // Open Panna inputs
  const [openPannaInput, setOpenPannaInput] = useState('');
  const [selectedOpenPanna, setSelectedOpenPanna] = useState('');
  const [filteredOpenPanna, setFilteredOpenPanna] = useState([]);
  const [showOpenPannaDropdown, setShowOpenPannaDropdown] = useState(false);
  const openPannaRef = useRef(null);
  
  // Close Panna inputs
  const [closePannaInput, setClosePannaInput] = useState('');
  const [selectedClosePanna, setSelectedClosePanna] = useState('');
  const [filteredClosePanna, setFilteredClosePanna] = useState([]);
  const [showClosePannaDropdown, setShowClosePannaDropdown] = useState(false);
  const closePannaRef = useRef(null);
  
  const [points, setPoints] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const dropdownRefs = [openDigitRef, closeDigitRef, openPannaRef, closePannaRef];

  const openTime = location.state?.openTime || '09:00';
  const closeTime = location.state?.closeTime || '18:00';
  const gameTypeLabel = 'Half Sangam';

  // Fetch game data from API
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://backend.mainbazar777.site/api/game-ank');
        const data = await response.json();
        // console.log('Fetched game data:', data);
        if (data.game_ank && data.game_ank.length > 0) {
          const gameData = data.game_ank[0];
          setAvailableDigits(gameData);
        }
        setError('');
      } catch (err) {
        setError('Failed to load game data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGameData();
  }, []);

  // Filter open digit (1 digit)
  useEffect(() => {
    if (!openDigitInput.trim()) {
      setFilteredOpenDigits([]);
      return;
    }

    if (Object.keys(availableDigits).length === 0) {
      setFilteredOpenDigits([]);
      return;
    }

    const digitList = availableDigits['half_sangam_open_digit'];
    if (!digitList) {
      setFilteredOpenDigits([]);
      return;
    }

    let parsed = digitList;
    if (typeof digitList === 'string') {
      try {
        parsed = JSON.parse(digitList);
      } catch (e) {
        console.error('Error parsing digit list:', e);
        setFilteredOpenDigits([]);
        return;
      }
    }

    let filtered = parsed.filter(digit => {
      const digitStr = digit.toString();
      return digitStr.length === 1 && /^\d+$/.test(digitStr);
    });

    filtered = filtered
      .filter(digit => digit.toString().includes(openDigitInput.trim()))
      .slice(0, 10);

    setFilteredOpenDigits(filtered);
  }, [openDigitInput, availableDigits]);

  // Filter close digit (1 digit)
  useEffect(() => {
    if (!closeDigitInput.trim()) {
      setFilteredCloseDigits([]);
      return;
    }

    if (Object.keys(availableDigits).length === 0) {
      setFilteredCloseDigits([]);
      return;
    }

    const digitList = availableDigits['half_sangam_open_digit'];
    if (!digitList) {
      setFilteredCloseDigits([]);
      return;
    }

    let parsed = digitList;
    if (typeof digitList === 'string') {
      try {
        parsed = JSON.parse(digitList);
      } catch (e) {
        console.error('Error parsing digit list:', e);
        setFilteredCloseDigits([]);
        return;
      }
    }

    let filtered = parsed.filter(digit => {
      const digitStr = digit.toString();
      return digitStr.length === 1 && /^\d+$/.test(digitStr);
    });

    filtered = filtered
      .filter(digit => digit.toString().includes(closeDigitInput.trim()))
      .slice(0, 10);

    setFilteredCloseDigits(filtered);
  }, [closeDigitInput, availableDigits]);

  // Filter open panna (3 digits)
  useEffect(() => {
    if (!openPannaInput.trim()) {
      setFilteredOpenPanna([]);
      return;
    }

    if (Object.keys(availableDigits).length === 0) {
      setFilteredOpenPanna([]);
      return;
    }

    const digitList = availableDigits['half_sangam_close_pana'];
    if (!digitList) {
      setFilteredOpenPanna([]);
      return;
    }

    let parsed = digitList;
    if (typeof digitList === 'string') {
      try {
        parsed = JSON.parse(digitList);
      } catch (e) {
        console.error('Error parsing digit list:', e);
        setFilteredOpenPanna([]);
        return;
      }
    }

    let filtered = parsed.filter(digit => {
      const digitStr = digit.toString();
      return digitStr.length === 3 && /^\d+$/.test(digitStr);
    });

    filtered = filtered
      .filter(digit => digit.toString().includes(openPannaInput.trim()))
      .slice(0, 10);

    setFilteredOpenPanna(filtered);
  }, [openPannaInput, availableDigits]);

  // Filter close panna (3 digits)
  useEffect(() => {
    if (!closePannaInput.trim()) {
      setFilteredClosePanna([]);
      return;
    }

    if (Object.keys(availableDigits).length === 0) {
      setFilteredClosePanna([]);
      return;
    }

    const digitList = availableDigits['half_sangam_close_pana'];
    if (!digitList) {
      setFilteredClosePanna([]);
      return;
    }

    let parsed = digitList;
    if (typeof digitList === 'string') {
      try {
        parsed = JSON.parse(digitList);
      } catch (e) {
        console.error('Error parsing digit list:', e);
        setFilteredClosePanna([]);
        return;
      }
    }

    let filtered = parsed.filter(digit => {
      const digitStr = digit.toString();
      return digitStr.length === 3 && /^\d+$/.test(digitStr);
    });

    filtered = filtered
      .filter(digit => digit.toString().includes(closePannaInput.trim()))
      .slice(0, 10);

    setFilteredClosePanna(filtered);
  }, [closePannaInput, availableDigits]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDigitRef.current && !openDigitRef.current.contains(event.target)) {
        setShowOpenDigitDropdown(false);
      }
      if (closeDigitRef.current && !closeDigitRef.current.contains(event.target)) {
        setShowCloseDigitDropdown(false);
      }
      if (openPannaRef.current && !openPannaRef.current.contains(event.target)) {
        setShowOpenPannaDropdown(false);
      }
      if (closePannaRef.current && !closePannaRef.current.contains(event.target)) {
        setShowClosePannaDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpenDigitSelect = (digit) => {
    setSelectedOpenDigit(digit);
    setOpenDigitInput('');
    setShowOpenDigitDropdown(false);
  };

  const handleCloseDigitSelect = (digit) => {
    setSelectedCloseDigit(digit);
    setCloseDigitInput('');
    setShowCloseDigitDropdown(false);
  };

  const handleOpenPannaSelect = (digit) => {
    setSelectedOpenPanna(digit);
    setOpenPannaInput('');
    setShowOpenPannaDropdown(false);
  };

  const handleClosePannaSelect = (digit) => {
    setSelectedClosePanna(digit);
    setClosePannaInput('');
    setShowClosePannaDropdown(false);
  };

  const handlePlaceBid = async () => {
    if (isMarketClosed()) {
      toast.error('Market Closed');
      if (navigator.vibrate) navigator.vibrate(200);
      navigate('/');
      return;
    }

    if (status === 'Open' && isOpenBidClosed()) {
      toast.error('Open bid time has closed. Please select Close.');
      return;
    }

    let bidOpenNumber, bidCloseNumber;

    if (status === 'Open') {
      if (!selectedOpenDigit || !selectedClosePanna || !points.trim()) {
        toast.error('Please select both fields and enter points');
        return;
      }
      bidOpenNumber = selectedOpenDigit;
      bidCloseNumber = selectedClosePanna;
    } else {
      if (!selectedOpenPanna || !selectedCloseDigit || !points.trim()) {
        toast.error('Please select both fields and enter points');
        return;
      }
      bidOpenNumber = selectedOpenPanna;
      bidCloseNumber = selectedCloseDigit;
    }

    try {
      const playerData = JSON.parse(localStorage.getItem('user') || '{}');
      const gameId = location.state?.gameId || '';
      const gameType = location.state?.gameTypeId || '';
      const gameNameFromState = location.state?.gameName || '';

      const formData = new FormData();
      formData.append('game_id', gameId);
      formData.append('game_name', gameNameFromState);
      formData.append('game_type', gameType);
      formData.append('player_id', playerData.id || '');
      formData.append('session', status.toLowerCase());
      formData.append('bid_amount', points);
      formData.append('bid_number', `${bidOpenNumber}${bidCloseNumber}`);

      const response = await fetch('https://backend.mainbazar777.site/api/play-game', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if(data.balance === false) {
        toast.error('Insufficient balance. Please add funds to your wallet.');
        return;
      } else if(response.ok && data.success) {
        toast.success(`Bid Placed: ${bidOpenNumber}-${bidCloseNumber} for ₹${points}`);
        
        // Refresh wallet balance after successful bid
        if (playerData?.id) {
          updateWalletBalance(playerData.id);
        }

        // Reset form
        setSelectedOpenDigit('');
        setSelectedCloseDigit('');
        setSelectedOpenPanna('');
        setSelectedClosePanna('');
        setPoints('');
        setOpenDigitInput('');
        setCloseDigitInput('');
        setOpenPannaInput('');
        setClosePannaInput('');
      } else {
        toast.error(`Failed to place bid for ${bidOpenNumber}-${bidCloseNumber}`);
      }
    } catch (err) {
      console.error('Error placing bid:', err);
      toast.error('Error placing bid. Please try again.');
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${String(displayHour).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const toMarketMinutes = (h, m) => ((h * 60 + m) - 120 + 1440) % 1440;

  const getKolkata = () => {
    const now = new Date();
    return new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  };

  const isOpenBidClosed = () => {
    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const [h, m] = openTime.split(':').map(Number);
    return current >= toMarketMinutes(h, m) - 5;
  };

  const isMarketClosed = () => {
    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const [h, m] = closeTime.split(':').map(Number);
    return current >= toMarketMinutes(h, m) - 5;
  };

  // Set initial status and auto-switch to Close when open bid time closes
  useEffect(() => {
    if (isOpenBidClosed()) {
      setStatus('Close');
      return;
    }
    setStatus('Open');
    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const secs = kt.getSeconds();
    const [h, m] = openTime.split(':').map(Number);
    const msUntilOpenClose = ((toMarketMinutes(h, m) - 5) - current) * 60000 - secs * 1000;
    const t = setTimeout(() => setStatus('Close'), msUntilOpenClose);
    return () => clearTimeout(t);
  }, [openTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-navigate home when market closes (5 min before close time)
  useEffect(() => {
    if (isMarketClosed()) {
      navigate('/');
      return;
    }
    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const secs = kt.getSeconds();
    const [h, m] = closeTime.split(':').map(Number);
    const msUntilClose = ((toMarketMinutes(h, m) - 5) - current) * 60000 - secs * 1000;
    const t = setTimeout(() => navigate('/'), msUntilClose);
    return () => clearTimeout(t);
  }, [closeTime, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = new Date();
  const dateString = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;

  if (loading) {
    return (
      <div className="game-entry">
        <header className="game-entry-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="game-title">{gameName || 'Loading...'}</h1>
          <div style={{ width: '24px' }}></div>
        </header>
        <div className="game-content">
          <div className="loading-message">Loading game data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-entry">
        <header className="game-entry-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="game-title">{gameName || 'Error'}</h1>
          <div style={{ width: '24px' }}></div>
        </header>
        <div className="game-content">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-entry">
      <header className="game-entry-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="game-title">{gameName || gameTypeLabel}</h1>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </header>

      <div className="game-content">
        <div className="game-info-section">
          <h2 className="game-type-heading">{gameTypeLabel}</h2>
          <div className="info-row">
            <div className="info-item">
              <label>Date</label>
              <input type="text" value={dateString} readOnly disabled />
            </div>
            <div className="info-item">
              <label>Status:</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="status-select"
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="Open" disabled={isOpenBidClosed()}>Open</option>
                <option value="Close">Close</option>
              </select>
            </div>
          </div>

          <div className="time-section">
            <div className="time-item">
              <span className="time-label">Time Open:</span>
              <span className="time-value">{formatTime(openTime)}</span>
            </div>
            <div className="time-item">
              <span className="time-label">Time Close:</span>
              <span className="time-value">{formatTime(closeTime)}</span>
            </div>
          </div>
        </div>

        <div className="input-section">
          <div className="input-row">
            {status === 'Open' ? (
              <>
                <div className="input-item" ref={openDigitRef}>
                  <label>Open Digit (1 Digit):</label>
                  <div className="dropdown-container">
                    <input
                      type="text"
                      placeholder="Enter Open Digit..."
                      value={openDigitInput || selectedOpenDigit}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
                        setOpenDigitInput(value);
                        setShowOpenDigitDropdown(true);
                        if (!value) {
                          setSelectedOpenDigit('');
                        }
                      }}
                      onFocus={() => setShowOpenDigitDropdown(true)}
                      className="digit-input"
                      autoComplete="off"
                    />
                    {showOpenDigitDropdown && filteredOpenDigits.length > 0 && (
                      <div className="dropdown-menu">
                        {filteredOpenDigits.map((digit, index) => (
                          <div
                            key={index}
                            className="dropdown-item"
                            onClick={() => handleOpenDigitSelect(digit)}
                          >
                            {digit}
                          </div>
                        ))}
                      </div>
                    )}
                    {showOpenDigitDropdown && openDigitInput && filteredOpenDigits.length === 0 && (
                      <div className="dropdown-menu">
                        <div className="dropdown-item disabled">No results found</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="input-item" ref={closePannaRef}>
                  <label>Close Panna (3 Digits):</label>
                  <div className="dropdown-container">
                    <input
                      type="text"
                      placeholder="Enter Close Panna..."
                      value={closePannaInput || selectedClosePanna}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                        setClosePannaInput(value);
                        setShowClosePannaDropdown(true);
                        if (!value) {
                          setSelectedClosePanna('');
                        }
                      }}
                      onFocus={() => setShowClosePannaDropdown(true)}
                      className="digit-input"
                      autoComplete="off"
                    />
                    {showClosePannaDropdown && filteredClosePanna.length > 0 && (
                      <div className="dropdown-menu">
                        {filteredClosePanna.map((digit, index) => (
                          <div
                            key={index}
                            className="dropdown-item"
                            onClick={() => handleClosePannaSelect(digit)}
                          >
                            {digit}
                          </div>
                        ))}
                      </div>
                    )}
                    {showClosePannaDropdown && closePannaInput && filteredClosePanna.length === 0 && (
                      <div className="dropdown-menu">
                        <div className="dropdown-item disabled">No results found</div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="input-item" ref={openPannaRef}>
                  <label>Open Panna (3 Digits):</label>
                  <div className="dropdown-container">
                    <input
                      type="text"
                      placeholder="Enter Open Panna..."
                      value={openPannaInput || selectedOpenPanna}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                        setOpenPannaInput(value);
                        setShowOpenPannaDropdown(true);
                        if (!value) {
                          setSelectedOpenPanna('');
                        }
                      }}
                      onFocus={() => setShowOpenPannaDropdown(true)}
                      className="digit-input"
                      autoComplete="off"
                    />
                    {showOpenPannaDropdown && filteredOpenPanna.length > 0 && (
                      <div className="dropdown-menu">
                        {filteredOpenPanna.map((digit, index) => (
                          <div
                            key={index}
                            className="dropdown-item"
                            onClick={() => handleOpenPannaSelect(digit)}
                          >
                            {digit}
                          </div>
                        ))}
                      </div>
                    )}
                    {showOpenPannaDropdown && openPannaInput && filteredOpenPanna.length === 0 && (
                      <div className="dropdown-menu">
                        <div className="dropdown-item disabled">No results found</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="input-item" ref={closeDigitRef}>
                  <label>Close Digit (1 Digit):</label>
                  <div className="dropdown-container">
                    <input
                      type="text"
                      placeholder="Enter Close Digit..."
                      value={closeDigitInput || selectedCloseDigit}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
                        setCloseDigitInput(value);
                        setShowCloseDigitDropdown(true);
                        if (!value) {
                          setSelectedCloseDigit('');
                        }
                      }}
                      onFocus={() => setShowCloseDigitDropdown(true)}
                      className="digit-input"
                      autoComplete="off"
                    />
                    {showCloseDigitDropdown && filteredCloseDigits.length > 0 && (
                      <div className="dropdown-menu">
                        {filteredCloseDigits.map((digit, index) => (
                          <div
                            key={index}
                            className="dropdown-item"
                            onClick={() => handleCloseDigitSelect(digit)}
                          >
                            {digit}
                          </div>
                        ))}
                      </div>
                    )}
                    {showCloseDigitDropdown && closeDigitInput && filteredCloseDigits.length === 0 && (
                      <div className="dropdown-menu">
                        <div className="dropdown-item disabled">No results found</div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="input-item">
              <label>Enter Funds:</label>
              <input
                type="number"
                placeholder="Funds"
                value={points}
                onChange={(e) => setPoints(e.target.value.replace(/[^0-9]/g, ''))}
                className="points-input"
              />
            </div>
          </div>

          <button className="add-btn" onClick={handlePlaceBid}>
            Place Bid
          </button>
        </div>
      </div>
    </div>
  );
}

export default HalfSangamGame;
